import { GetReportStaysRequest, ReportStaysRequest } from "../infrastructure/reportStays.api";
import { asUTC, currentDate, dateToString } from "../../../lib/dateUtils";
import { TableColumns, TableFilters } from "../../shared/crud/list";
import { ReportStaysEntity } from "../domain/reportStays.entity";
import { BooleanRenderer, NumberRenderer, StringRenderer } from "../../shared/renderers/table";
import { FormattedMessage, useIntl } from "react-intl";
import { useMemo } from "react";
import { dateFilter, selectFilter, selectorFilter } from "../../shared/crud/filter-widgets";
import { addDays, parseISO } from "date-fns";
import { reportStaysFilterMessages } from "../messages";
import { useUserPropertyTickers } from "../../../components/user-tickers-providers";
import { formatMessage } from "../../../lib/intlUtils";

interface ReportStaysCellCSV {
  message: { id: string; defaultMessage: string };
  value: (entity: ReportStaysEntity) => string;
}

type ReportStaysFilters = Partial<ReportStaysRequest> & {
  period: string;
  tickets: boolean;
  stays: boolean;
  departures: boolean;
  showComments: boolean;
  order_1: string;
  order_2: string;
  order_3: string;
};

const defaultRequest = {
  checkIn: dateToString(addDays(currentDate(), 1)),
  checkOut: dateToString(addDays(currentDate(), 1)),
  groupBy: ["checkIn"],
  orderBy: {},
  statusConfig: [0, 2, 5],
};

export function generateReportStaysHeaders(showComments: boolean): TableColumns<ReportStaysEntity> {
  const headers = {
    date: {
      renderer: StringRenderer,
      width: 125,
    },
    dayOfTheWeek: {
      renderer: StringRenderer,
      width: 100,
    },
    action: {
      renderer: StringRenderer,
      width: 125,
    },
    currentNight: {
      renderer: StringRenderer,
      width: 75,
    },
    totalNights: {
      renderer: NumberRenderer,
      width: 75,
    },
    bookingId: {
      renderer: StringRenderer,
      width: 125,
    },
    multipleBookingId: {
      renderer: StringRenderer,
      width: 175,
    },
    customerName: {
      renderer: StringRenderer,
      width: 200,
    },
    rooms: {
      renderer: NumberRenderer,
      width: 100,
    },
    inventoryTicker: {
      renderer: StringRenderer,
      width: 200,
    },
    capacity: {
      renderer: NumberRenderer,
      width: 100,
    },
    extras: {
      renderer: StringRenderer,
      width: 250,
    },
  }
  return showComments ? { ...headers, comments: { renderer: BooleanRenderer, width: 125 } } : headers;
}

export function useReportStaysFilters(): TableFilters<ReportStaysFilters, ReportStaysFilters> {
  const intl = useIntl();
  const propertyTickers = useUserPropertyTickers();
  return useMemo(() => {
    if (!propertyTickers.length) {
      return {};
    }

    const filters: TableFilters<ReportStaysFilters, ReportStaysFilters> = {
      period: {
        filterFn: "async",
        widget: dateFilter("period", intl),
      },
      hotelTickers: {
        filterFn: "async",
        widget: selectorFilter(
          <FormattedMessage {...reportStaysFilterMessages.hotelTickers} />,
          propertyTickers.map(it => ({ value: it, label: it }))
        )
      },
      tickets: {
        filterFn: "async",
        widget: selectFilter(intl, reportStaysFilterMessages.tickets),
      },
      stays: {
        filterFn: "async",
        widget: selectFilter(intl, reportStaysFilterMessages.stays),
      },
      departures: {
        filterFn: "async",
        widget: selectFilter(intl, reportStaysFilterMessages.departures),
      },
      showComments: {
        filterFn: "async",
        widget: selectFilter(intl, reportStaysFilterMessages.showComments),
      },
    };

    Array.from({ length: 3 }).forEach((_, i) => {
      filters[`order_${i}`] = {
        filterFn: "async",
        widget: selectorFilter(
          <FormattedMessage {...reportStaysFilterMessages.order} values={{ index: i + 1 }} />,
          [
            { value: "empty", label: "" },
            { value: "date", label: intl.formatMessage(reportStaysFilterMessages.date) },
            { value: "dayOfTheWeek", label: intl.formatMessage(reportStaysFilterMessages.dayOfTheWeek) },
            { value: "currentNight", label: intl.formatMessage(reportStaysFilterMessages.currentNight) },
            { value: "totalNights", label: intl.formatMessage(reportStaysFilterMessages.totalNights) },
            { value: "bookingId", label: intl.formatMessage(reportStaysFilterMessages.bookingId) },
            { value: "multipleBookingId", label: intl.formatMessage(reportStaysFilterMessages.multipleBookingId) },
            { value: "customerName", label: intl.formatMessage(reportStaysFilterMessages.customerName) },
            { value: "rooms", label: intl.formatMessage(reportStaysFilterMessages.rooms) },
            { value: "roomTicker", label: intl.formatMessage(reportStaysFilterMessages.inventoryTicker) },
            { value: "capacity", label: intl.formatMessage(reportStaysFilterMessages.capacity) },
            { value: "extras", label: intl.formatMessage(reportStaysFilterMessages.extras) },
          ]
        )
      };
      filters[`sorting_${i}`] = {
        filterFn: "async",
        widget: selectorFilter(
          <FormattedMessage {...reportStaysFilterMessages.asc} />,
          [
            { value: "asc", label: intl.formatMessage(reportStaysFilterMessages.asc) },
            { value: "desc", label: intl.formatMessage(reportStaysFilterMessages.desc) },
          ]
        )
      }
    });

    return filters;
  }, [intl, propertyTickers]);
}

export function generateReportStaysRequest({ defaultPropertyTicker, filterValues, pageSize, currentPage }): GetReportStaysRequest {
  const orderBy: {[key: string]: "asc" | "desc"} = defaultRequest.orderBy;
  Array.from({ length: 3 })
    .filter((_, i) => filterValues?.[`order_${i}`])
    .forEach((_, i) => {
      orderBy[filterValues[`order_${i}`]] = filterValues?.[`sorting_${i}`] || "asc"
    });
  const groupBy: string[] = [];
  if (filterValues?.tickets && filterValues.tickets === "true") {
    groupBy.push("checkIn");
  }
  if (filterValues?.stays && filterValues.stays === "true") {
    groupBy.push("stay");
  }
  if (filterValues?.departures && filterValues.departures === "true") {
    groupBy.push("checkOut");
  }
  return {
    request: {
      checkIn: filterValues?.period?.startDate
        ? asUTC(parseISO(filterValues.period.startDate), "yyyy-MM-dd")
        : defaultRequest.checkIn,
      checkOut: filterValues?.period?.endDate
        ? asUTC(parseISO(filterValues.period.endDate), "yyyy-MM-dd")
        : defaultRequest.checkOut,
      groupBy: groupBy.length ? groupBy : defaultRequest.groupBy,
      hotelTickers: filterValues?.hotelTickers?.split(",") ?? [defaultPropertyTicker],
      orderBy,
      showComments: Boolean(filterValues?.showComments && filterValues.showComments === "true"),
      statusConfig: defaultRequest.statusConfig,
    },
    size: pageSize,
    page: currentPage,
  }
}

export function generateReportStaysCsv(report: ReportStaysEntity[], locale: string, showComments: boolean): string {
  const headers = generateReportStaysHeaders(showComments);
  const cellDefinitions: ReportStaysCellCSV[] = Object.keys(headers).map(it => ({
    message: reportStaysFilterMessages[it],
    value: (entity) => entity[it],
  }));
  const rowToCsv = (report: ReportStaysEntity) =>
    cellDefinitions.map(it => it.value(report)).join(";");

  let rows = `${cellDefinitions.map(it => formatMessage(it.message, locale)).join(";")}\n`;
  rows += report.map(it => rowToCsv(it)).join("\n");

  return URL.createObjectURL(new Blob([rows], { type: "text/csv" }));
}
